// components/ScraperDashboard/LogSummary.js - At-a-glance log metrics
import React from 'react';
import {
  Activity, AlertCircle, Zap, Clock, Wifi, WifiOff
} from 'lucide-react';
import './LogSummary.css';

function LogSummary({ stats, connected, logs }) {
  // Calculate derived stats from logs if server stats not available
  const derivedStats = React.useMemo(() => {
    if (!logs || logs.length === 0) {
      return {
        activeJobs: 0,
        lastMinute: 0,
        errorCount: 0,
        lastError: null,
        recentOperations: []
      };
    }

    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    const lastMinuteLogs = logs.filter(l =>
      new Date(l.timestamp).getTime() > oneMinuteAgo
    );

    const errorLogs = logs.filter(l => l.level === 'error');
    const activeJobs = new Set(
      lastMinuteLogs
        .filter(l => l.context?.jobId)
        .map(l => l.context.jobId)
    );
    const recentOperations = new Set(
      lastMinuteLogs
        .filter(l => l.context?.operation)
        .map(l => l.context.operation)
    );

    return {
      activeJobs: activeJobs.size,
      lastMinute: lastMinuteLogs.length,
      errorCount: errorLogs.length,
      lastError: errorLogs[0] || null,
      recentOperations: Array.from(recentOperations)
    };
  }, [logs]);

  const displayStats = stats || derivedStats;

  // Calculate processing rate (logs per minute)
  const processingRate = displayStats.lastMinute || derivedStats.lastMinute;

  return (
    <div className="ls-container">
      {/* Connection Status */}
      <div className={`ls-connection ${connected ? 'connected' : 'disconnected'}`}>
        {connected ? <Wifi size={14} /> : <WifiOff size={14} />}
        <span>{connected ? 'Live' : 'Offline'}</span>
      </div>

      {/* Active Jobs */}
      <div className="ls-stat">
        <Activity size={14} className="ls-icon ls-icon-primary" />
        <span className="ls-value">{displayStats.activeJobs || 0}</span>
        <span className="ls-label">Active Jobs</span>
      </div>

      {/* Processing Rate */}
      <div className="ls-stat">
        <Zap size={14} className="ls-icon ls-icon-accent" />
        <span className="ls-value">{processingRate}</span>
        <span className="ls-label">Logs/min</span>
      </div>

      {/* Error Count */}
      <div className={`ls-stat ${displayStats.errorCount > 0 ? 'has-errors' : ''}`}>
        <AlertCircle size={14} className="ls-icon ls-icon-error" />
        <span className="ls-value">{displayStats.errorCount || derivedStats.errorCount}</span>
        <span className="ls-label">Errors</span>
      </div>

      {/* Recent Operations */}
      {(displayStats.recentOperations?.length > 0 || derivedStats.recentOperations.length > 0) && (
        <div className="ls-operations">
          {(displayStats.recentOperations || derivedStats.recentOperations).map(op => (
            <span key={op} className="ls-operation-badge">{op}</span>
          ))}
        </div>
      )}

      {/* Last Error Preview */}
      {(displayStats.lastError || derivedStats.lastError) && (
        <div className="ls-last-error">
          <AlertCircle size={12} />
          <span className="ls-error-message">
            {(displayStats.lastError || derivedStats.lastError).message?.slice(0, 50)}
            {(displayStats.lastError || derivedStats.lastError).message?.length > 50 ? '...' : ''}
          </span>
          <span className="ls-error-time">
            <Clock size={10} />
            {new Date((displayStats.lastError || derivedStats.lastError).timestamp).toLocaleTimeString()}
          </span>
        </div>
      )}
    </div>
  );
}

export default LogSummary;
